import { useState, useEffect, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Textarea,
  Typography,
} from "@mui/joy";
import SearchIcon from "@mui/icons-material/Search";
import {
  buildProviderCredentialPayload,
  getProviderLabel,
  hasRequiredProviderCredentials,
  normalizeProviderKey,
} from "../../../helpers/modelProviders";

const INITIAL_STATE = {
  name: "",
  display_name: "",
  provider: "",
  api_key: "",
  aws_access_key: "",
  aws_secret_key: "",
  aws_region: "us-east-1",
  deployment_url: "",
  google_service_account_json: "",
};

const CreateModelModal = ({ open, onClose, onCreateModel, onDiscoverModels, providers: providersProp = [], providersLoading = false }) => {
  // Ensure providers is always an array
  const providers = useMemo(() => Array.isArray(providersProp) ? providersProp : [], [providersProp]);

  const [formData, setFormData] = useState(INITIAL_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveredModels, setDiscoveredModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);

  useEffect(() => {
    if (open) {
      const firstProvider = providers[0];
      const defaultProvider = providers.length > 0
        ? (typeof firstProvider === "string" ? firstProvider : firstProvider?.value || "")
        : "";
      setFormData({ ...INITIAL_STATE, provider: defaultProvider });
      setDiscoveredModels([]);
      setSelectedModel(null);
    }
  }, [open, providers]);

  // Set default provider when providers load and no provider is selected
  useEffect(() => {
    if (providers.length > 0 && !formData.provider) {
      const firstProvider = providers[0];
      const defaultProvider = typeof firstProvider === "string" ? firstProvider : firstProvider?.value || "";
      setFormData((prev) => ({ ...prev, provider: defaultProvider }));
    }
  }, [providers, formData.provider]);

  // Reset discovered models when provider changes
  useEffect(() => {
    setDiscoveredModels([]);
    setSelectedModel(null);
    setFormData((prev) => ({ ...prev, name: "", display_name: "" }));
  }, [formData.provider]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value ?? e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClose = () => {
    setFormData(INITIAL_STATE);
    setDiscoveredModels([]);
    setSelectedModel(null);
    onClose();
  };

  const buildCredentialsPayload = () => ({
    provider: formData.provider,
    ...buildProviderCredentialPayload({
      provider: formData.provider,
      values: formData,
      operation: "discover",
    }),
  });

  const handleDiscover = async () => {
    setIsDiscovering(true);
    setDiscoveredModels([]);
    setSelectedModel(null);

    const credentials = buildCredentialsPayload();
    const result = await onDiscoverModels(credentials);

    setIsDiscovering(false);

    if (result.success && result.data?.models) {
      setDiscoveredModels(result.data.models);
    }
  };

  const handleModelSelect = (_, value) => {
    if (typeof value === "string") {
      setSelectedModel(null);
      setFormData((prev) => ({
        ...prev,
        name: value,
      }));
      return;
    }

    setSelectedModel(value);
    if (value) {
      setFormData((prev) => ({
        ...prev,
        name: value.name,
        display_name: value.display_name || value.name,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        name: "",
      }));
    }
  };

  const handleModelInputChange = (_, inputValue, reason) => {
    if (reason !== "input" && reason !== "clear") {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      name: inputValue,
    }));

    if (selectedModel && inputValue !== selectedModel.name) {
      setSelectedModel(null);
    }
  };

  const buildPayload = () => {
    const { name, display_name, provider } = formData;

    return {
      name,
      display_name,
      provider,
      ...buildProviderCredentialPayload({
        provider,
        values: formData,
        operation: "create",
      }),
    };
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      return;
    }

    setIsSubmitting(true);
    const payload = buildPayload();
    const result = await onCreateModel(payload);
    setIsSubmitting(false);

    if (result.success) {
      handleClose();
    }
  };

  const canDiscover = () => {
    return hasRequiredProviderCredentials({
      provider: formData.provider,
      values: formData,
      operation: "discover",
    });
  };

  const renderProviderFields = () => {
    switch (normalizeProviderKey(formData.provider)) {
      case "openai":
        return (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>API Key</FormLabel>
            <Input
              type="password"
              placeholder="sk-..."
              value={formData.api_key}
              onChange={handleChange("api_key")}
            />
          </FormControl>
        );

      case "bedrock":
        return (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>AWS Access Key</FormLabel>
              <Input
                placeholder="AKIA..."
                value={formData.aws_access_key}
                onChange={handleChange("aws_access_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>AWS Secret Key</FormLabel>
              <Input
                type="password"
                placeholder="Enter AWS secret key"
                value={formData.aws_secret_key}
                onChange={handleChange("aws_secret_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>AWS Region</FormLabel>
              <Input
                placeholder="us-east-1"
                value={formData.aws_region}
                onChange={handleChange("aws_region")}
              />
            </FormControl>
          </>
        );

      case "azure":
        return (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>API Key</FormLabel>
              <Input
                type="password"
                placeholder="Enter Azure API key"
                value={formData.api_key}
                onChange={handleChange("api_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Deployment URL</FormLabel>
              <Input
                placeholder="https://myresource.openai.azure.com"
                value={formData.deployment_url}
                onChange={handleChange("deployment_url")}
              />
            </FormControl>
          </>
        );

      case "azure_document_intelligence":
        return (
          <>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>API Key</FormLabel>
              <Input
                type="password"
                placeholder="Enter Azure Document Intelligence API key"
                value={formData.api_key}
                onChange={handleChange("api_key")}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Deployment URL</FormLabel>
              <Input
                placeholder="https://<resource>.cognitiveservices.azure.com/"
                value={formData.deployment_url}
                onChange={handleChange("deployment_url")}
              />
            </FormControl>
          </>
        );

      case "google_vision":
        return (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Google Service Account JSON</FormLabel>
            <Textarea
              minRows={6}
              placeholder={'{ "type": "service_account", ... }'}
              value={formData.google_service_account_json}
              onChange={handleChange("google_service_account_json")}
            />
          </FormControl>
        );

      case "ollama":
      case "lm_studio":
        return (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Deployment URL</FormLabel>
            <Input
              placeholder={normalizeProviderKey(formData.provider) === "ollama" ? "http://localhost:11434" : "http://localhost:1234/v1"}
              value={formData.deployment_url}
              onChange={handleChange("deployment_url")}
            />
          </FormControl>
        );

      default:
        return null;
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={handleClose}>
      <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto', width: 400, bgcolor: 'background.surface' }}>
        <Typography level="h4" sx={{ mb: 1 }}>
          Add New Model
        </Typography>
        <Typography textColor="text.tertiary" sx={{ mb: 2 }}>
          Configure a new AI model for your agents.
        </Typography>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Provider</FormLabel>
          <Select
            value={formData.provider}
            onChange={(_, value) => handleChange("provider")(value)}
            disabled={providersLoading || providers.length === 0}
            startDecorator={providersLoading ? <CircularProgress size="sm" /> : null}
            placeholder={providersLoading ? "Loading providers..." : "Select a provider"}
          >
            {providers.map((provider) => {
              const providerValue = typeof provider === "string" ? provider : provider?.value;
              const providerLabel = typeof provider === "string" ? null : provider?.label;
              if (!providerValue) {
                return null;
              }

              return (
                <Option key={providerValue} value={providerValue}>
                  {providerLabel || getProviderLabel(providerValue)}
                </Option>
              );
            })}
          </Select>
          {!providersLoading && providers.length === 0 && (
            <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
              No providers available
            </Typography>
          )}
        </FormControl>

        {renderProviderFields()}

        {/* Discover Button */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="neutral"
            fullWidth
            startDecorator={isDiscovering ? <CircularProgress size="sm" /> : <SearchIcon />}
            onClick={handleDiscover}
            disabled={!canDiscover() || isDiscovering}
          >
            {isDiscovering ? "Discovering..." : "Discover Available Models"}
          </Button>
          {!canDiscover() && (
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              Enter credentials above to discover models
            </Typography>
          )}
        </Box>

        {/* Model Selection */}
        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Model</FormLabel>
          <Autocomplete
            freeSolo
            sx={{ cursor: 'pointer' }}
            placeholder={discoveredModels.length > 0 ? "Select or type a model" : "e.g., gpt-4o, llama3:latest"}
            options={discoveredModels}
            value={selectedModel}
            inputValue={formData.name}
            onInputChange={handleModelInputChange}
            onChange={handleModelSelect}
            getOptionLabel={(option) => {
              if (typeof option === "string") {
                return option;
              }
              return option.display_name || option.name;
            }}
            isOptionEqualToValue={(option, value) => option.name === value?.name}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ flexDirection: 'column', alignItems: 'flex-start !important', py: 1.5 }}>
                <Typography level="body-sm" fontWeight="md">
                  {option.display_name || option.name}
                </Typography>
                <Typography level="body-xs" color="neutral">
                  {option.name}
                </Typography>
              </Box>
            )}
          />
          {discoveredModels.length > 0 && (
            <>
              <Typography level="body-xs" color="success" sx={{ mt: 0.5 }}>
                {discoveredModels.length} model(s) found
              </Typography>
              <Typography level="body-xs" color="neutral" sx={{ mt: 0.25 }}>
                You can select from the list or type a custom model name.
              </Typography>
            </>
          )}
        </FormControl>

        <FormControl sx={{ mb: 2 }}>
          <FormLabel>Display Name</FormLabel>
          <Input
            placeholder="e.g., GPT-4o, Llama 3"
            value={formData.display_name}
            onChange={handleChange("display_name")}
          />
        </FormControl>
      </Box>

      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'neutral.outlinedBorder', position: 'sticky', bottom: 0, bgcolor: 'background.surface' }}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button onClick={handleClose} variant="plain">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="solid"
            color="primary"
            loading={isSubmitting}
            disabled={!formData.name.trim() || !formData.provider}
          >
            Create Model
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CreateModelModal;
